import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Divider,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTourDates } from '../../../context/TourDateContext';
import { IExpandedTourDate } from '../../tour-offer/models/IExpandedTourDate';

type EditableTourDate = IExpandedTourDate & { availableSpotsInput: string };

const ManageTourAvailabilityPage = () => {
    const { getAllDates } = useTourDates();
    const [groupedByYear, setGroupedByYear] = useState<Record<number, EditableTourDate[]>>({});
    const [loadingId, setLoadingId] = useState<number | null>(null);
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState<Record<number, boolean>>({});

    useEffect(() => {
        const dates = getAllDates();

        const grouped = dates.reduce<Record<number, EditableTourDate[]>>((acc, date) => {
            const editable: EditableTourDate = {
                ...date,
                availableSpotsInput: String(date.availableSpots),
            };
            if (!acc[date.year]) acc[date.year] = [];
            acc[date.year].push(editable);
            return acc;
        }, {});

        setGroupedByYear(grouped);
    }, [getAllDates]);

    const handleChange = (year: number, id: number, value: string) => {
        setGroupedByYear((prev) => ({
            ...prev,
            [year]: prev[year].map((tour) =>
                tour.id === id ? { ...tour, availableSpotsInput: value } : tour
            ),
        }));

        const parsed = Number(value);
        setErrors((prev) => ({
            ...prev,
            [id]: value === '' || isNaN(parsed) || parsed < 0,
        }));
    };

    const handleUpdate = async (id: number, year: number) => {
        const tour = groupedByYear[year].find((t) => t.id === id);
        if (!tour) return;

        const parsed = Number(tour.availableSpotsInput);
        if (
            tour.availableSpotsInput === '' ||
            isNaN(parsed) ||
            parsed < 0
        ) {
            setErrors((prev) => ({ ...prev, [id]: true }));
            return;
        }

        setLoadingId(id);
        try {
            const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/api/tour-dates/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ availableSpots: parsed }),
            });

            if (!res.ok) throw new Error('Greška pri ažuriranju');

            setSuccess(true);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingId(null);
        }
    };

    return (
        <Container sx={{ mt: 10, p: 0 }}>
            <Typography variant="h5" gutterBottom>
                Upravljanje slobodnim mjestima
            </Typography>

            {Object.entries(groupedByYear).map(([yearStr, tourDates]) => {
                const year = Number(yearStr);
                return (
                    <Box key={year} sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Termini za {year}
                        </Typography>

                        <Stack spacing={2}>
                            {tourDates.map((date) => (
                                <Card key={date.id}>
                                    <CardContent sx={{ pt: 1, position: "relative", }}>
                                        <Typography variant="caption" color="text2.primary" sx={{ position: 'absolute', top: 10, right: 16 }}>
                                            {date.tour.name}
                                        </Typography>
                                        <Typography fontWeight={600}>
                                            📅 {format(new Date(date.startDate), 'dd.MM.yyyy')} – {format(new Date(date.endDate), 'dd.MM.yyyy')}
                                        </Typography>

                                        <Box mt={2} display="flex" gap={2} alignItems="center" width={"100%"}>
                                            <TextField
                                                type="number"
                                                label="Slobodna mjesta"
                                                value={date.availableSpotsInput}
                                                onChange={(e) =>
                                                    handleChange(date.year, date.id, e.target.value)
                                                }
                                                size="small"
                                                sx={{ width: "100%" }}
                                                error={!!errors[date.id]}
                                                helperText={errors[date.id] ? 'Unos mora biti broj ≥ 0' : ''}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={() => handleUpdate(date.id, date.year)}
                                                disabled={loadingId === date.id}
                                            >
                                                {loadingId === date.id ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    'Spremi'
                                                )}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>

                        <Divider sx={{ mt: 3 }} />
                    </Box>
                );
            })}

            <Snackbar
                open={success}
                autoHideDuration={3000}
                onClose={() => setSuccess(false)}
                message="Uspješno ažurirano!"
            />
        </Container>
    );
};

export default ManageTourAvailabilityPage;
